import dayjs from 'dayjs';
import { v4 as uuid } from 'uuid';

import {
  GITHUB_REGEX,
  HireFormSchema,
  LINKEDIN_REGEX,
  LinkFormSchema,
  RelocateFormSchema,
} from '../widgets/schema';
import {
  ParsedEducationFormSchema,
  ParsedExperienceFormSchema,
  ParsedLanguageFormSchema,
  ParsedMainFormSchema,
  ParsedSkillFormSchema,
  ParseRateFormSchema,
} from '../widgets/schemaParsed';

const normalizeDateString = (dateStr: any): null | string => {
  if (!dateStr) return null;

  const dateFormats = [
    'YYYY-MM-DD',
    'DD/MM/YYYY',
    'DD-MM-YYYY',
    'DD.MM.YYYY',
    'MM/DD/YYYY',
    'MM-DD-YYYY',
    'MMMM DD, YYYY',
    'DD MMMM YYYY',
    'MMM DD, YYYY',
    'YYYY',
  ];

  for (const format of dateFormats) {
    const parsed = dayjs(dateStr, format, true);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD');
    }
  }

  const parsed = dayjs(dateStr);
  if (parsed.isValid()) {
    return parsed.format('YYYY-MM-DD');
  }

  return null;
};

const preprocessParsedData = (data: any) => {
  const result = { ...data };

  if (Array.isArray(result?.experience)) {
    result.experience = result.experience.map((exp: any) => {
      const start = normalizeDateString(exp.start);
      const end = exp.ongoing ? null : normalizeDateString(exp.end);

      return {
        company: exp.company ?? '',
        description: exp.description ?? '',
        end,
        id: exp.id || uuid(),
        location: exp.location ?? '',
        ongoing: exp.ongoing ?? !end,
        start,
        title: exp.title ?? '',
      };
    });
  }

  if (Array.isArray(result?.education)) {
    result.education = result.education.map((edu: any) => {
      const start = normalizeDateString(edu.start);
      const end = edu.ongoing ? null : normalizeDateString(edu.end);

      return {
        degree: edu.degree ?? '',
        description: edu.description ?? '',
        end,
        id: edu.id || uuid(),
        location: edu.location ?? '',
        ongoing: edu.ongoing ?? !end,
        program: edu.program ?? 'N/A',
        school: edu.school ?? '',
        start,
      };
    });
  }

  if (Array.isArray(result?.languages)) {
    result.languages = result.languages.map((lang: any) => ({
      id: lang.id || uuid(),
      language: lang.language ?? '',
      rate: typeof lang.rate === 'string' ? parseInt(lang.rate, 10) : (lang.rate ?? 1),
    }));
  }

  if (Array.isArray(result?.links)) {
    result.links = result.links.map((link: any) => {
      let label = 'other';
      let url = link.url ?? '';

      if (GITHUB_REGEX.test(url)) {
        label = 'github';
      } else if (LINKEDIN_REGEX.test(url)) {
        label = 'linkedin';
      }

      if (label === 'other' && url && !url.startsWith('https://')) {
        url = `https://${url.replace(/^http:\/\//, '')}`;
      }

      return {
        id: link.id || uuid(),
        label,
        url,
      };
    });
  }

  if (Array.isArray(result?.skills)) {
    result.skills = result.skills.map((skill: any) => ({
      id: skill.id || uuid(),
      rate: typeof skill.rate === 'string' ? parseInt(skill.rate, 10) : (skill.rate ?? 5),
      skill: skill.skill ?? '',
    }));
  }

  return result;
};

export const validateParsedData = (parsedData: any) => {
  const preprocessedData = preprocessParsedData(parsedData);

  let validData: any = {};

  const mainResult = ParsedMainFormSchema.safeParse({ ...preprocessedData, isVisible: true });
  if (mainResult.success) {
    validData = { ...validData, ...mainResult.data };
  }

  const hireResult = HireFormSchema.safeParse(preprocessedData);
  if (hireResult.success) {
    validData = { ...validData, ...hireResult.data };
  }

  const relocateResult = RelocateFormSchema.safeParse(preprocessedData);
  if (relocateResult.success) {
    validData = { ...validData, ...relocateResult.data };
  }

  const rateResult = ParseRateFormSchema.safeParse(preprocessedData);
  if (rateResult.success) {
    validData = { ...validData, ...rateResult.data };
  }

  const validLanguages = preprocessedData?.languages
    ?.map((language: any) => {
      const result = ParsedLanguageFormSchema.safeParse(language);
      if (!result.success) return null;
      return result.data;
    })
    .filter(Boolean);
  if (validLanguages?.length > 0) {
    validData.languages = validLanguages;
  }

  const validExperiences = preprocessedData?.experience
    ?.map((exp: any) => {
      const result = ParsedExperienceFormSchema.safeParse(exp);
      if (!result.success) return null;
      return result.data;
    })
    .filter(Boolean);
  if (validExperiences?.length > 0) {
    validData.experience = validExperiences;
  }

  const validEducation = preprocessedData?.education
    ?.map((edu: any) => {
      const result = ParsedEducationFormSchema.safeParse(edu);
      if (!result.success) return null;
      return result.data;
    })
    .filter(Boolean);
  if (validEducation?.length > 0) {
    validData.education = validEducation;
  }

  const validLinks = preprocessedData?.links
    ?.map((link: any) => {
      const result = LinkFormSchema.safeParse(link);
      if (!result.success) return null;
      return result.data;
    })
    .filter(Boolean);
  if (validLinks?.length > 0) {
    validData.links = validLinks;
  }

  const validSkills = preprocessedData?.skills
    ?.map((skill: any) => {
      const result = ParsedSkillFormSchema.safeParse(skill);
      if (!result.success) return null;
      return result.data;
    })
    .filter(Boolean);
  if (validSkills?.length > 0) {
    validData.skills = validSkills;
  }

  return validData;
};
